import { useQuery } from '@tanstack/react-query'
import { getAnalyticsSuppliers, type AnalyticsPeriodParams } from '../../shared/api/analytics.api'
import { Spinner } from '../../shared/ui/Spinner'
import { ErrorMessage } from '../../shared/ui/ErrorMessage'
import { useAnalyticsTable, type ColumnDef } from './shared/useAnalyticsTable'
import { SortableTh } from './shared/SortableTh'

/**
 * Вкладка «Поставщики» модуля «Аналитика» (этап 5 docs/analytics-plan.md).
 * Реестр-воронка: запрошено → ответил → в заказ, response rate, win rate.
 * Сортировка по всем колонкам + фильтр по поставщику.
 */

type SupplierRow = {
  supplier: string
  requested: number
  answered: number
  responseRate: number
  won: number
  winRate: number
}

/** Цвет response rate: высокий — зелёный. */
const rrClass = (pct: number): string =>
  pct >= 60 ? 'text-emerald-600' : pct >= 30 ? 'text-amber-600' : 'text-red-600'

const COLUMNS: ColumnDef<SupplierRow>[] = [
  { key: 'supplier', sort: (r) => r.supplier.toLowerCase(), filter: (r) => r.supplier },
  { key: 'requested', sort: (r) => r.requested },
  { key: 'answered', sort: (r) => r.answered },
  { key: 'responseRate', sort: (r) => r.responseRate },
  { key: 'won', sort: (r) => r.won },
  { key: 'winRate', sort: (r) => r.winRate },
]

export function SuppliersTab({ periodParams }: { periodParams: AnalyticsPeriodParams }) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['analytics-suppliers', periodParams],
    queryFn: () => getAnalyticsSuppliers(periodParams),
  })

  const table = useAnalyticsTable(
    (data?.rows ?? []) as SupplierRow[],
    COLUMNS,
    undefined,
    'suppliers',
  )

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner />
      </div>
    )
  }
  if (isError || !data) {
    return <ErrorMessage error="Не удалось загрузить аналитику по поставщикам" />
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <h3 className="text-sm font-semibold text-gray-900">Поставщики — реестр-воронка</h3>
      <p className="text-xs text-gray-500 mt-0.5">
        Запрошено позиций → ответил → ушло в заказ. Источник — квоты по всем матрицам (запрошено —
        позиции с квотой поставщика, ответил — квоты с ценой). Response rate — доля ответов, win
        rate — доля выигранных от отвеченных.
      </p>
      <div className="mt-3 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-gray-500 border-b border-gray-200">
              <SortableTh table={table} col="supplier" className="pr-3">
                Поставщик
              </SortableTh>
              <SortableTh table={table} col="requested" align="right" className="px-3">
                Запрошено
              </SortableTh>
              <SortableTh table={table} col="answered" align="right" className="px-3">
                Ответил
              </SortableTh>
              <SortableTh table={table} col="responseRate" align="right" className="px-3">
                Response rate
              </SortableTh>
              <SortableTh table={table} col="won" align="right" className="px-3">
                В заказ
              </SortableTh>
              <SortableTh table={table} col="winRate" align="right" className="pl-3">
                Win rate
              </SortableTh>
            </tr>
          </thead>
          <tbody>
            {table.view.map((r) => (
              <tr key={r.supplier} className="border-b border-gray-50">
                <td className="py-2 pr-3 font-medium text-gray-800">{r.supplier}</td>
                <td className="py-2 px-3 text-right text-gray-800">{r.requested}</td>
                <td className="py-2 px-3 text-right text-gray-600">{r.answered}</td>
                <td className={`py-2 px-3 text-right font-medium ${rrClass(r.responseRate)}`}>
                  {r.responseRate}%
                </td>
                <td className="py-2 px-3 text-right text-gray-600">{r.won}</td>
                <td className="py-2 pl-3 text-right font-medium text-gray-700">{r.winRate}%</td>
              </tr>
            ))}
            {table.view.length === 0 && (
              <tr>
                <td colSpan={6} className="py-6 text-center text-sm text-gray-400">
                  Нет запросов поставщикам за период
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <p className="mt-3 text-xs text-gray-400">
        Win rate считается по полю orderedQuoteId (этап 2): по историческим данным может быть
        пустым, наполняется при оформлении новых заказов.
      </p>
    </div>
  )
}
